import React, { useEffect, useRef, useState, useCallback } from "react";
import type { UPIData } from "../types";
import { UPIScanner } from "../scanner";

export interface QRScannerProps {
  /** Called when a valid UPI QR is successfully scanned. */
  onScan: (data: UPIData) => void;
  /** Called on scanner errors (camera permission, library load, etc). */
  onError?: (error: Error) => void;
  /** Called when the user clicks the close button (if shown). */
  onClose?: () => void;
  /** Width of the scanner viewport in pixels. */
  width?: number;
  /** Height of the scanner viewport in pixels. */
  height?: number;
  /** FPS for the camera feed. */
  fps?: number;
  /** Whether to show a close button. */
  showCloseButton?: boolean;
  /** CSS class for the container. */
  className?: string;
  /** Inline styles for the container. */
  style?: React.CSSProperties;
  /** Debounce interval in ms to prevent duplicate scans. */
  debounceMs?: number;
}

/**
 * React component that renders a camera-based QR scanner.
 *
 * Only UPI QR codes are emitted via `onScan`; non-UPI codes are ignored.
 *
 * @example
 * ```tsx
 * import { QRScanner } from "@vouch/sdk/react";
 *
 * <QRScanner
 *   onScan={(upi) => console.log(upi.upiId)}
 *   onError={(e) => toast.error(e.message)}
 * />
 * ```
 */
export const QRScanner: React.FC<QRScannerProps> = ({
  onScan,
  onError,
  onClose,
  width = 320,
  height = 320,
  fps = 10,
  showCloseButton = true,
  className,
  style,
  debounceMs = 2000,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scannerRef = useRef<UPIScanner | null>(null);
  const onScanRef = useRef(onScan);
  const onErrorRef = useRef(onError);
  const [loading, setLoading] = useState(true);
  const [cameraError, setCameraError] = useState<string | null>(null);

  onScanRef.current = onScan;
  onErrorRef.current = onError;

  const stableOnScan = useCallback((data: UPIData) => onScanRef.current(data), []);
  const stableOnError = useCallback((err: Error) => {
    setCameraError(err.message);
    setLoading(false);
    onErrorRef.current?.(err);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const scanner = new UPIScanner({
      container: containerRef.current,
      onScan: stableOnScan,
      onError: stableOnError,
      width,
      height,
      fps,
      debounceMs,
    });

    scannerRef.current = scanner;

    scanner
      .start()
      .then(() => setLoading(false))
      .catch(() => setLoading(false));

    return () => {
      scanner.stop().catch(() => {});
    };
  }, [width, height, fps, debounceMs, stableOnScan, stableOnError]);

  return (
    <div
      className={className}
      style={{
        position: "relative",
        width,
        height,
        background: "#000",
        borderRadius: 8,
        overflow: "hidden",
        ...style,
      }}
    >
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            zIndex: 2,
          }}
        >
          <span>Starting camera…</span>
        </div>
      )}

      {cameraError && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#ff6b6b",
            padding: 16,
            textAlign: "center",
            zIndex: 2,
          }}
        >
          <p>{cameraError}</p>
          {showCloseButton && onClose && (
            <button onClick={onClose} style={{ marginTop: 12 }}>
              Close
            </button>
          )}
        </div>
      )}

      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

      {showCloseButton && onClose && !cameraError && (
        <button
          onClick={onClose}
          aria-label="Close scanner"
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 3,
            background: "rgba(0,0,0,0.5)",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            padding: "4px 8px",
            cursor: "pointer",
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
};
